import { useCallback, useState } from 'react';
import type { ServerBackupsResponse, ServerView } from '@hearth/shared';
import { ApiError, type Api } from '../api';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useCached } from './cache';
import { cardKind, errorText } from './serverWords';

// The Backups tab (M10.14, owners): a backup is taken each time the server falls asleep; restoring
// one replaces the world at the next wake. Only while it's asleep; a pending restore can be cancelled.

type Backup = ServerBackupsResponse['backups'][number];

/** "Today, 21:40", "Yesterday, 23:05", "18 October, 22:30" (local time). */
export function backupWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(at)) / 86_400_000);
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  const date = at.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', ...(at.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
  return `${date}, ${time}`;
}

/** "412 MB", "1.2 GB". */
export function backupSize(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

/** What restoring asks: a warning when the last sleep may not have saved the world. */
export function restoreText(server: ServerView, when: string): string {
  const unclean = server.lastStopClean === false ? ' Its last sleep may not have saved the world, so what was played since then could already be lost.' : '';
  // "Today, 21:40" → "today at 21:40"; "18 October, 22:30" → "on 18 October at 22:30".
  const [day, time] = when.split(', ');
  const at = day === 'Today' || day === 'Yesterday' ? `${day.toLowerCase()} at ${time}` : `on ${day} at ${time}`;
  return `Next time ${server.name} wakes, its world goes back to how it was ${at}. Backups stay in this list.${unclean}`;
}

export function BackupsTab({ api, server, onChanged, onGone }: { api: Api; server: ServerView; onChanged: () => Promise<void>; onGone: () => void }) {
  const [asking, setAsking] = useState<Backup>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const base = `/v1/servers/${encodeURIComponent(server.serverId)}`;

  const refused = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) onGone();
      else setError(errorText(err));
    },
    [onGone],
  );

  // Kept between tabs (cache.ts); read again when it's old, a sleep adds a backup, or a restore is asked for or done.
  const backups = useCached(`backups:${server.serverId}`, async () => (await api.get<ServerBackupsResponse>(`${base}/backups`)).backups, {
    version: `${server.lastBackupAt ?? ''} ${server.restorePending}`,
    failed: refused,
  }).data;

  async function send(change: () => Promise<unknown>) {
    setBusy(true);
    try {
      await change();
      setError('');
      await onChanged();
    } catch (err) {
      refused(err);
    } finally {
      setBusy(false);
      setAsking(undefined);
    }
  }

  return (
    <>
      {error && <p className="notice" role="alert">{error}</p>}
      <BackupsPanel
        server={server}
        backups={backups}
        now={new Date()}
        busy={busy}
        onRestore={setAsking}
        onCancel={() => void send(() => api.delete(`${base}/restore`))}
      />
      <ConfirmDialog
        open={!!asking}
        title="Restore this backup?"
        confirmLabel="Restore"
        tone="danger"
        busy={busy}
        // An unclean last sleep: the API refuses unless forced, and the question already warned.
        onConfirm={() => asking && void send(() => api.post(`${base}/restore`, { key: asking.id, ...(server.lastStopClean === false ? { force: true } : {}) }))}
        onCancel={() => setAsking(undefined)}
      >
        {asking ? restoreText(server, backupWhen(asking.takenAt, new Date())) : ''}
      </ConfirmDialog>
    </>
  );
}

/** The backups panel, given what's loaded (no fetching: easy to test). `backups` undefined = loading. */
export function BackupsPanel(props: {
  server: ServerView;
  backups: Backup[] | undefined;
  now: Date;
  busy: boolean;
  onRestore: (backup: Backup) => void;
  onCancel: () => void;
}) {
  const { server, backups, now, busy, onRestore, onCancel } = props;
  const asleep = cardKind(server) === 'asleep' && server.status !== 'DESTROYED';
  return (
    <section className="panel panel-backups">
      <h2>Backups</h2>
      <p className="quiet">Taken every time it falls asleep. Restoring replaces the world as it is now.</p>
      {server.restorePending && (
        <div className="restore-pending" role="status">
          <span>A restore is waiting: it happens when {server.name} next wakes.</span>
          {asleep && (
            <button className="quiet-btn" type="button" disabled={busy} onClick={onCancel}>Cancel restore</button>
          )}
        </div>
      )}
      {!asleep && server.status !== 'DESTROYED' && backups?.length ? <p className="quiet">Put it to sleep to restore a backup.</p> : null}
      {!backups ? (
        <p className="quiet" aria-live="polite">Loading…</p>
      ) : backups.length === 0 ? (
        <p className="quiet">No backups yet: one is taken each time it falls asleep.</p>
      ) : (
        <ul className="backups">
          {backups.map((b, i) => (
            <li key={b.id}>
              <span className="backup-when">
                {backupWhen(b.takenAt, now)}
                {i === 0 && <span className="quiet"> · Latest</span>}
              </span>
              <span className="quiet backup-size">{backupSize(b.bytes)}</span>
              {asleep && (
                <button className="btn btn-small" type="button" disabled={busy} aria-label={`Restore the backup from ${backupWhen(b.takenAt, now)}`} onClick={() => onRestore(b)}>
                  Restore
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
